import { isBoolean } from 'lodash';

import { getRegisteredClass, registerClass } from '../SatCoreRegistry';

import Auth from '../managers/AuthManager';
import accommodationsManager from '../managers/AccommodationsManager';
import adminUsersManager from '../managers/AdminUsersManager';
import appManager from '../managers/AppManager';
import assignmentManager from '../managers/AssignmentManager';
import basicIframeModalLauncherManager from '../managers/BasicIframeModalLauncherManager';
import classroomManager from '../managers/ClassroomManager';
import contentManager from '../managers/ContentManager';
import contextHelpManager from '../managers/ContextHelpManager';
import courseDeliveryManager from '../managers/CourseDeliveryManager';
import courseManager from '../managers/CourseManager';
import courseResourceFilterManager from '../managers/CourseResourceFilterManager';
import dialogManager from '../managers/DialogManager';
import dynamicSatelliteManager from '../managers/DynamicSatelliteManager';
import googleAnalyticsManager from '../managers/GoogleAnalyticsManager';
import googleClassroomManager from '../managers/GoogleClassroomManager';
import gradebookManager from '../managers/GradebookManager';
import guidedTourManager from '../managers/GuidedTourManager';
import libraryManager from '../managers/LibraryManager';
import loginManager from '../managers/LoginManager';
import reportContextManager from '../managers/reports/ReportContextManager';
import reportOverrideManager from '../managers/reports/ReportOverrideManager';
import schoolManager from '../managers/SchoolManager';
import studentAssignmentManager from '../managers/StudentAssignmentManager';
import studentContentCardManager from '../managers/StudentContentCardManager';
import studentCourseDrivenExperienceManager from '../managers/StudentCourseDrivenExperienceManager';
import studentProgressManager from '../managers/StudentProgressManager';
import thirdPartyManager from '../managers/ThirdPartyManager';
import topNavManager from '../managers/TopNavManager';
import userManager from '../managers/UserManager';
import verticalCourseTOCManager from '../managers/VerticalCourseTOCManager';

import {
  getCssUrlArgument, getSessionStorageItem, getStyleVar, isWhiteStyleColor, setSessionStorageItem
} from '../utils';
import { generateRobustUrl } from '../utils/url';

import { addSatellite as i18nAddSatellite } from '../i18n';

export default class DynamicSatelliteService {
  static fetchDynamicSatelliteByDomain = async () => {
    try {
      const InitService = getRegisteredClass('InitService');

      const dynamicSatelliteResponseData = await dynamicSatelliteManager.fetchDynamicSatelliteByDomain();

      const {
        googleAnalyticsId,
        reportPrefix,
        satelliteCode,
        satelliteCssUrl,
        satelliteJsonUrl,
        satelliteLayoutUrl,
        satelliteName,
        satelliteUrl
      } = dynamicSatelliteResponseData;

      // Set Google Analytics ID if provided
      if (googleAnalyticsId) {
        googleAnalyticsManager.setGoogleAnalyticsId(googleAnalyticsId);
      }

      // TODO - useful for local development if you use HTTPS localhost; change the above 'const' declaration to 'let' and
      // - set these values to the files you copy to react-sales-demo public folder for the dynamic satellite
      // - you want to use.
      // satelliteCssUrl = '/demo.css';
      // satelliteJsonUrl = '/demoTranslations.json';
      // satelliteLayoutUrl = '/demoSatelliteLayout.json';

      Auth.setPublisherSatelliteCode(satelliteCode);
      Auth.setPublisherSatelliteUrl(satelliteUrl);
      userManager.setClientPrefix(reportPrefix);
      setSessionStorageItem('dynamicPublisherSatelliteCode', satelliteCode);

      // TODO useful for local development if you have a lot of Sirius data
      // TODO ---
      // TODO remember to comment back out before committing any changes
      // Auth.setPublisherSatelliteCode('SIRIUS_SATELLITE');
      // userManager.setClientPrefix('SIRIUS');
      // setSessionStorageItem('dynamicPublisherSatelliteCode', 'SIRIUS_SATELLITE');

      // TODO remove
      // console.info('dynamicSatelliteResponseData', dynamicSatelliteResponseData);

      // TODO remove
      // console.info('satelliteCssUrl', satelliteCssUrl);

      document.title = satelliteName;
      appManager.setSatelliteName(satelliteName);

      const dynamicSatelliteLayoutConfig = await this.initDynamicSatelliteLayoutConfig({ satelliteLayoutUrl });

      await this.initDynamicSatelliteFlagOverrides(dynamicSatelliteLayoutConfig);

      await this.initDynamicSatelliteTranslations({ satelliteJsonUrl });

      this.initDynamicSatelliteStyleSheet({ satelliteCssUrl });

      setTimeout(() => {
        this.initDynamicFavIconIfApplicable();
        InitService.initLocale();
      }, 500);
    } catch (error) {
      console.error(error);
    }
  }

  static initDynamicSatelliteLayoutConfig = async ({ satelliteLayoutUrl } = {}) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    const dynamicLayoutResponse = satelliteLayoutUrl ? await fetch(satelliteLayoutUrl, {
      headers,
      method: 'GET',
      redirect: 'follow'
    }) : undefined;

    const dynamicSatelliteLayoutConfig = await dynamicLayoutResponse?.json?.();
    dynamicSatelliteManager.setDynamicSatelliteLayoutConfig(dynamicSatelliteLayoutConfig);

    return dynamicSatelliteLayoutConfig;
  }

  /**
   * Initialize dynamic satellite override flags that we would typically set in an `index.js` file
   * for a given non-dynamic satellite.
   *
   * Note this method is intended to be used for **dynamic satellites only**.
   *
   * The `dynamicSatelliteLayoutConfig.satelliteIndexFlagOverrides` object will be looked at
   * to determine which flag overrides should be set.
   */
  static initDynamicSatelliteFlagOverrides = async (dynamicSatelliteLayoutConfig) => {
    const flagOverrides = dynamicSatelliteLayoutConfig?.satelliteIndexFlagOverrides;
    if (!flagOverrides || typeof flagOverrides !== 'object') {
      return;
    }

    const {
      additionalAdminTopNavExternalResources,
      additionalLoginExternalResources,
      additionalLtiTopNavExternalResources,
      additionalStudentTopNavExternalResources,
      additionalTeacherTopNavExternalResources,
      adminSchoolCardOptionsOrderArray,
      allowAdminUserProfileChangePasswordForNonStudents,
      allowAdminUserProfileChangePasswordForStudents,
      allowAdminUsersAddNewTeacher,
      allowClassroomContentLauncherButton,
      allowClassroomCourseCardDragDropReorder,
      allowCompactClassroomCourseCard,
      allowContentItemDescriptionIcon,
      allowCourseResourceFiltering,
      allowCourseTreeLeafGradebookButton,
      allowDebugCourseResourceFiltering,
      allowDigitalItemBanking,
      allowFetchLibraryDataIfNoLicensedBanks,
      allowGoogleClassroomIntegration,
      allowGradebookContextSummaryOnly,
      allowGradebookRowActionFeedback,
      allowGradebookStandards,
      allowObservationalGrader,
      allowPresentationMode,
      allowPreserveCourseElementResourceScrollPosition,
      allowPrintItemBanking,
      allowRedeemLicenseCodeButton,
      allowReportCmapStandardsAttributeFiltersDropdown,
      allowReportCmapStandardsDropdown,
      allowReportCmapStandardsGroupFilter,
      allowReportCmapStandardsMoreFiltersDropdown,
      allowReportHideColumnsResetAllAsUnselectedAction,
      allowReportHideColumnsResetAllToOriginalSelectionsAction,
      allowReportHideRowsResetAllAsUnselectedAction,
      allowReportHideRowsResetAllToOriginalSelectionsAction,
      allowReportStandards,
      allowSsoAddExistingStudentToSelfRegClassroom,
      allowSsoCreateSelfRegClassroom,
      allowStudentBooks,
      allowStudentCourseDrivenAssignmentFiltering,
      allowStudentCourseDrivenCarousel,
      allowStudentCourseDrivenExperience,
      allowTeacherAssignmentListLauncherButton,
      allowTeacherAssignmentShowAllLauncherButton,
      allowTeacherClassroomHeaderCustomActionButtons,
      allowTeacherLaunchAsSpecificStudent,
      allowUseCmapId,
      allowVerticalCourseTOC,
      allowedReportCmapStandardsAttributeFilterTypes,
      allowedStudentDashboardProgressWidgets,
      allowedStudentProgressWidgets,
      allowedThirdPartyChatLauncherUserRoles,
      alwaysExcludeStudentAssignmentCardAlignmentPills,
      avatarPickerFacultyName,
      avatarPickerStudentName,
      basicIframeModalLauncherUrlMapEntries,
      classCardInfoOrderArray,
      classWorkspaceFeature,
      courseTreeLeafButtonTypeOrderArray,
      customAllowedReportCutScores,
      customEndpointsForGoogleClassroom,
      customEndpointsForUser,
      customReportCutScores,
      demolinkTeacherMainTopNavId,
      demolinkTeacherTopNavOrderArray,
      gradebookContextOrderArray,
      gradebookSummaryTableCellOrderArray,
      guidedTourConfig,
      allowPlacement,
      placementCategoryId,
      customPlacementCutScores,
      hideUnscorableColumnsByDefault,
      hideAddExistingStudentButtonForTeachers,
      hideGradebookColorKeys,
      hideReportsTab,
      hideUserInstitutionsListFromProfile,
      includeAlignments,
      includeClassroomAccommodations,
      includeGroups,
      isAlwaysReadOnlyUserInstitutionsListFromProfile,
      isUserProfileReadOnly,
      maxReportFacultyScoreCellsPerPage,
      profileMenuDistrictAdminOrderArray,
      profileMenuGenericOrderArray,
      profileMenuSchoolAdminOrderArray,
      profileMenuStudentOrderArray,
      profileMenuTeacherOrderArray,
      reportContextTypeDefaultOrderArray,
      reportTypeSelectorViewIconOverride,
      restrictAvatar,
      restrictAvatarForAllUserTypes,
      allowSsoViewClassroomSettings,
      shouldAlwaysOpenKebabOnProductLineLibraryCardClick,
      shouldOverrideBackendDefaultClassroomImageWithCustomStyling,
      shouldRedirectAddCourseLauncherButtonToLibrary,
      shouldShowStandardsReportSummaryBar,
      showGradebookKeyViewMoreModal,
      showManualScoringToolbarItem,
      showResourcePacingToolbar,
      studentAssignmentMenuStatusOrderArray,
      studentContentCardDateFormatStrategy,
      onlyShowLessonTrophies,
      studentProgressTrophyTiers,
      teacherAssignmentDefaultMenuStatus,
      teacherAssignmentMenuStatusOrderArray,
      teacherClassroomCardFooterNavButtonOrderArray,
      testBuilderContextHelpId,
      thirdPartyChatLauncherCustomVariables,
      thirdPartyChatLauncherUrl,
      useAssignmentTabRollovers,
      useProductLineLibrary,
      wordToolsTTSDependent
    } = flagOverrides;

    if (Array.isArray(additionalAdminTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('admin', additionalAdminTopNavExternalResources);
    }
    if (Array.isArray(additionalLoginExternalResources)) {
      loginManager.setAdditionalLoginExternalResources(additionalLoginExternalResources);
    }
    if (Array.isArray(additionalLtiTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('lti', additionalLtiTopNavExternalResources);
    }
    if (Array.isArray(additionalStudentTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('student', additionalStudentTopNavExternalResources);
    }
    if (Array.isArray(additionalTeacherTopNavExternalResources)) {
      topNavManager.setAdditionalTopNavExternalResources('teacher', additionalTeacherTopNavExternalResources);
    }
    if (Array.isArray(adminSchoolCardOptionsOrderArray)) {
      schoolManager.setAdminSchoolCardOptionsOrderArray(adminSchoolCardOptionsOrderArray);
    }
    if (isBoolean(allowAdminUserProfileChangePasswordForNonStudents)) {
      adminUsersManager.setAllowAdminUserProfileChangePasswordForNonStudents(allowAdminUserProfileChangePasswordForNonStudents);
    }
    if (isBoolean(allowAdminUserProfileChangePasswordForStudents)) {
      adminUsersManager.setAllowAdminUserProfileChangePasswordForStudents(allowAdminUserProfileChangePasswordForStudents);
    }
    if (isBoolean(allowAdminUsersAddNewTeacher)) {
      adminUsersManager.setAllowAdminUsersAddNewTeacher(allowAdminUsersAddNewTeacher);
    }
    if (isBoolean(allowClassroomContentLauncherButton)) {
      classroomManager.setAllowClassroomContentLauncherButton(allowClassroomContentLauncherButton);
    }
    if (isBoolean(allowClassroomCourseCardDragDropReorder)) {
      courseManager.setAllowClassroomCourseCardDragDropReorder(allowClassroomCourseCardDragDropReorder);
    }
    if (isBoolean(allowCompactClassroomCourseCard)) {
      courseManager.setAllowCompactClassroomCourseCard(allowCompactClassroomCourseCard);
    }
    if (isBoolean(allowContentItemDescriptionIcon)) {
      courseDeliveryManager.setAllowContentItemDescriptionIcon(allowContentItemDescriptionIcon);
    }
    if (isBoolean(allowCourseResourceFiltering)) {
      courseResourceFilterManager.setAllowCourseResourceFiltering(allowCourseResourceFiltering);
    }
    if (isBoolean(allowCourseTreeLeafGradebookButton)) {
      courseManager.setAllowCourseTreeLeafGradebookButton(allowCourseTreeLeafGradebookButton);
    }
    if (isBoolean(allowDebugCourseResourceFiltering)) {
      courseResourceFilterManager.setAllowDebugCourseResourceFiltering(allowDebugCourseResourceFiltering);
    }
    if (isBoolean(allowDigitalItemBanking)) {
      libraryManager.setAllowDigitalItemBanking(allowDigitalItemBanking);
    }
    if (isBoolean(allowFetchLibraryDataIfNoLicensedBanks)) {
      libraryManager.setAllowFetchLibraryDataIfNoLicensedBanks(allowFetchLibraryDataIfNoLicensedBanks);
    }
    if (isBoolean(allowGoogleClassroomIntegration)) {
      googleClassroomManager.setAllowGoogleClassroomIntegration(allowGoogleClassroomIntegration);
    }
    if (isBoolean(allowGradebookContextSummaryOnly)) {
      gradebookManager.setAllowGradebookContextSummaryOnly(allowGradebookContextSummaryOnly);
    }
    if (isBoolean(allowGradebookRowActionFeedback)) {
      gradebookManager.setAllowGradebookRowActionFeedback(allowGradebookRowActionFeedback);
    }
    if (isBoolean(allowGradebookStandards)) {
      gradebookManager.setAllowGradebookStandards(allowGradebookStandards);
    }
    if (Array.isArray(gradebookContextOrderArray)) {
      gradebookManager.setGradebookContextOrderArray(gradebookContextOrderArray);
    }
    if (Array.isArray(gradebookSummaryTableCellOrderArray)) {
      gradebookManager.setGradebookSummaryTableCellOrderArray(gradebookSummaryTableCellOrderArray);
    }
    if (isBoolean(allowObservationalGrader)) {
      gradebookManager.setAllowObservationalGrader(allowObservationalGrader);
    }
    if (isBoolean(allowPresentationMode)) {
      assignmentManager.setAllowPresentationMode(allowPresentationMode);
    }
    if (isBoolean(allowPreserveCourseElementResourceScrollPosition)) {
      courseManager.setAllowPreserveCourseElementResourceScrollPosition(allowPreserveCourseElementResourceScrollPosition);
    }
    if (isBoolean(allowPrintItemBanking)) {
      libraryManager.setAllowPrintItemBanking(allowPrintItemBanking);
    }
    if (isBoolean(allowRedeemLicenseCodeButton)) {
      libraryManager.setAllowRedeemLicenseCodeButton(allowRedeemLicenseCodeButton);
    }
    if (isBoolean(allowReportHideColumnsResetAllAsUnselectedAction)) {
      reportOverrideManager.setAllowReportHideColumnsResetAllAsUnselectedAction(
        allowReportHideColumnsResetAllAsUnselectedAction
      );
    }
    if (isBoolean(allowReportHideColumnsResetAllToOriginalSelectionsAction)) {
      reportOverrideManager.setAllowReportHideColumnsResetAllToOriginalSelectionsAction(
        allowReportHideColumnsResetAllToOriginalSelectionsAction
      );
    }
    if (isBoolean(allowReportHideRowsResetAllAsUnselectedAction)) {
      reportOverrideManager.setAllowReportHideRowsResetAllAsUnselectedAction(
        allowReportHideRowsResetAllAsUnselectedAction
      );
    }
    if (isBoolean(allowReportHideRowsResetAllToOriginalSelectionsAction)) {
      reportOverrideManager.setAllowReportHideRowsResetAllToOriginalSelectionsAction(
        allowReportHideRowsResetAllToOriginalSelectionsAction
      );
    }
    if (isBoolean(allowReportCmapStandardsAttributeFiltersDropdown)) {
      reportOverrideManager.setAllowReportCmapStandardsAttributeFiltersDropdown(allowReportCmapStandardsAttributeFiltersDropdown);
    }
    if (isBoolean(allowReportCmapStandardsDropdown)) {
      reportOverrideManager.setAllowReportCmapStandardsDropdown(allowReportCmapStandardsDropdown);
    }
    if (isBoolean(allowReportCmapStandardsGroupFilter)) {
      reportOverrideManager.setAllowReportCmapStandardsGroupFilter(allowReportCmapStandardsGroupFilter);
    }
    if (isBoolean(allowReportCmapStandardsMoreFiltersDropdown)) {
      reportOverrideManager.setAllowReportCmapStandardsMoreFiltersDropdown(allowReportCmapStandardsMoreFiltersDropdown);
    }
    if (isBoolean(allowReportStandards)) {
      reportOverrideManager.setAllowReportStandards(allowReportStandards);
    }
    if (isBoolean(allowUseCmapId)) {
      reportOverrideManager.setAllowUseCmapId(allowUseCmapId);
    }
    if (isBoolean(allowVerticalCourseTOC)) {
      verticalCourseTOCManager.setAllowVerticalCourseTOC(allowVerticalCourseTOC);
    }
    if (Array.isArray(allowedReportCmapStandardsAttributeFilterTypes)) {
      reportOverrideManager.setAllowedReportCmapStandardsAttributeFilterTypes(allowedReportCmapStandardsAttributeFilterTypes);
    }
    if (customAllowedReportCutScores && typeof customAllowedReportCutScores === 'object') {
      reportOverrideManager.setCustomAllowedReportCutScores(customAllowedReportCutScores);
    }
    if (isBoolean(allowSsoAddExistingStudentToSelfRegClassroom)) {
      classroomManager.setAllowSsoAddExistingStudentToSelfRegClassroom(allowSsoAddExistingStudentToSelfRegClassroom);
    }
    if (isBoolean(allowSsoCreateSelfRegClassroom)) {
      classroomManager.setAllowSsoCreateSelfRegClassroom(allowSsoCreateSelfRegClassroom);
    }
    if (isBoolean(allowStudentBooks)) {
      courseManager.setAllowStudentBooks(allowStudentBooks);
    }
    if (isBoolean(allowStudentCourseDrivenAssignmentFiltering)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenAssignmentFiltering(allowStudentCourseDrivenAssignmentFiltering);
    }
    if (isBoolean(allowStudentCourseDrivenCarousel)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenCarousel(allowStudentCourseDrivenCarousel);
    }
    if (isBoolean(allowStudentCourseDrivenExperience)) {
      studentCourseDrivenExperienceManager.setAllowStudentCourseDrivenExperience(allowStudentCourseDrivenExperience);
    }
    if (isBoolean(allowTeacherClassroomHeaderCustomActionButtons)) {
      classroomManager.setAllowTeacherClassroomHeaderCustomActionButtons(allowTeacherClassroomHeaderCustomActionButtons);
    }
    if (isBoolean(allowTeacherLaunchAsSpecificStudent)) {
      gradebookManager.setAllowTeacherLaunchAsSpecificStudent(allowTeacherLaunchAsSpecificStudent);
    }
    if (Array.isArray(allowedStudentDashboardProgressWidgets)) {
      studentProgressManager.setAllowedStudentDashboardProgressWidgets(allowedStudentDashboardProgressWidgets);
    }
    if (Array.isArray(allowedStudentProgressWidgets)) {
      studentProgressManager.setAllowedStudentProgressWidgets(allowedStudentProgressWidgets);
    }
    if (Array.isArray(studentProgressTrophyTiers) && studentProgressTrophyTiers.length > 0) {
      studentProgressManager.setStudentProgressTrophyTiers(studentProgressTrophyTiers);
    }
    if (isBoolean(onlyShowLessonTrophies)) {
      studentProgressManager.setOnlyShowLessonTrophies(onlyShowLessonTrophies);
    }
    if (Array.isArray(allowedThirdPartyChatLauncherUserRoles)) {
      thirdPartyManager.setAllowedThirdPartyChatLauncherUserRoles(allowedThirdPartyChatLauncherUserRoles);
    }
    if (isBoolean(alwaysExcludeStudentAssignmentCardAlignmentPills)) {
      studentAssignmentManager.setAlwaysExcludeStudentAssignmentCardAlignmentPills(
        alwaysExcludeStudentAssignmentCardAlignmentPills
      );
    }
    if ('avatarPickerFacultyName' in flagOverrides) {
      userManager.setAvatarPickerFacultyName(avatarPickerFacultyName);
    }
    if ('avatarPickerStudentName' in flagOverrides) {
      userManager.setAvatarPickerStudentName(avatarPickerStudentName);
    }
    if (Array.isArray(basicIframeModalLauncherUrlMapEntries)) {
      basicIframeModalLauncherManager.setBasicIframeModalLauncherUrlMap(basicIframeModalLauncherUrlMapEntries);
    }
    if (isBoolean(classWorkspaceFeature)) {
      appManager.classWorkspaceFeature = classWorkspaceFeature;
    }
    if (Array.isArray(classCardInfoOrderArray)) {
      classroomManager.setClassCardInfoOrderArray(classCardInfoOrderArray);
    }
    if (Array.isArray(courseTreeLeafButtonTypeOrderArray)) {
      courseManager.setCourseTreeLeafButtonTypeOrderArray(courseTreeLeafButtonTypeOrderArray);
    }
    if (typeof customEndpointsForGoogleClassroom === 'object') {
      googleClassroomManager.setCustomEndpointsForGoogleClassroom(customEndpointsForGoogleClassroom);
    }
    if (typeof customEndpointsForUser === 'object') {
      userManager.setCustomEndpointsForUser(customEndpointsForUser);
    }
    if (customReportCutScores && typeof customReportCutScores === 'object') {
      reportOverrideManager.setCustomReportCutScores(customReportCutScores);
    }
    if (typeof guidedTourConfig === 'object') {
      guidedTourManager.setGuidedTourConfig(guidedTourConfig);
    }
    if (isBoolean(hideAddExistingStudentButtonForTeachers)) {
      classroomManager.setHideAddExistingStudentButtonForTeachers(hideAddExistingStudentButtonForTeachers);
    }
    if (isBoolean(allowPlacement)) {
      gradebookManager.setAllowPlacement(allowPlacement);
    }
    if (placementCategoryId) {
      gradebookManager.setPlacementCategoryId(placementCategoryId);
    }
    if (Array.isArray(customPlacementCutScores) ? customPlacementCutScores.length > 0
      : (customPlacementCutScores !== null && typeof customPlacementCutScores === 'object')) {
      gradebookManager.setCustomPlacementCutScores(customPlacementCutScores);
    }
    if (isBoolean(hideUnscorableColumnsByDefault)) {
      gradebookManager.setHideUnscorableColumnsByDefault(hideUnscorableColumnsByDefault);
    }
    if (isBoolean(hideGradebookColorKeys)) {
      gradebookManager.setHideGradebookColorKeys(hideGradebookColorKeys);
    }
    if (isBoolean(hideReportsTab)) {
      userManager.setHideReportsTab(hideReportsTab);
    }
    if (isBoolean(hideUserInstitutionsListFromProfile)) {
      userManager.setHideUserInstitutionsListFromProfile(hideUserInstitutionsListFromProfile);
    }
    if (isBoolean(includeAlignments)) {
      assignmentManager.setIncludeAlignments(includeAlignments);
      courseManager.setIncludeAlignments(includeAlignments);
    }
    if (isBoolean(includeClassroomAccommodations)) {
      accommodationsManager.setIncludeClassroomAccommodations(includeClassroomAccommodations);
    }
    if (isBoolean(includeGroups)) {
      classroomManager.setIncludeGroups(includeGroups);
    }
    if (isBoolean(isAlwaysReadOnlyUserInstitutionsListFromProfile)) {
      userManager.setIsAlwaysReadOnlyUserInstitutionsListFromProfile(isAlwaysReadOnlyUserInstitutionsListFromProfile);
    }
    if (isBoolean(isUserProfileReadOnly)) {
      userManager.setIsUserProfileReadOnly(isUserProfileReadOnly);
    }
    if (typeof maxReportFacultyScoreCellsPerPage === 'number') {
      reportOverrideManager.setMaxReportFacultyScoreCellsPerPage(maxReportFacultyScoreCellsPerPage);
    }
    if (Array.isArray(profileMenuDistrictAdminOrderArray)) {
      topNavManager.setProfileMenuDistrictAdminOrderArray(profileMenuDistrictAdminOrderArray);
    }
    if (Array.isArray(profileMenuGenericOrderArray)) {
      topNavManager.setProfileMenuGenericOrderArray(profileMenuGenericOrderArray);
    }
    if (Array.isArray(profileMenuSchoolAdminOrderArray)) {
      topNavManager.setProfileMenuSchoolAdminOrderArray(profileMenuSchoolAdminOrderArray);
    }
    if (Array.isArray(profileMenuStudentOrderArray)) {
      topNavManager.setProfileMenuStudentOrderArray(profileMenuStudentOrderArray);
    }
    if (Array.isArray(profileMenuTeacherOrderArray)) {
      topNavManager.setProfileMenuTeacherOrderArray(profileMenuTeacherOrderArray);
    }
    // TODO: Uncomment to add demo link support for other roles
    // if (Array.isArray(demolinkDistrictAdminTopNavOrderArray)) {
    //   topNavManager.setDemolinkDistrictAdminTopNavOrderArray(demolinkDistrictAdminTopNavOrderArray);
    // }
    // if (typeof demolinkDistrictAdminMainTopNavId === 'string') {
    //   topNavManager.setDemolinkDistrictAdminMainTopNavId(demolinkDistrictAdminMainTopNavId);
    // }
    // if (Array.isArray(demolinkSchoolAdminTopNavOrderArray)) {
    //   topNavManager.setDemolinkSchoolAdminTopNavOrderArray(demolinkSchoolAdminTopNavOrderArray);
    // }
    // if (typeof demolinkSchoolAdminMainTopNavId === 'string') {
    //   topNavManager.setDemolinkSchoolAdminMainTopNavId(demolinkSchoolAdminMainTopNavId);
    // }
    // if (Array.isArray(demolinkStudentTopNavOrderArray)) {
    //   topNavManager.setDemolinkStudentTopNavOrderArray(demolinkStudentTopNavOrderArray);
    // }
    // if (typeof demolinkStudentMainTopNavId === 'string') {
    //   topNavManager.setDemolinkStudentMainTopNavId(demolinkStudentMainTopNavId);
    // }
    if (Array.isArray(demolinkTeacherTopNavOrderArray)) {
      topNavManager.setDemolinkTeacherTopNavOrderArray(demolinkTeacherTopNavOrderArray);
    }
    if (typeof demolinkTeacherMainTopNavId === 'string') {
      topNavManager.setDemolinkTeacherMainTopNavId(demolinkTeacherMainTopNavId);
    }
    if (Array.isArray(reportContextTypeDefaultOrderArray)) {
      reportOverrideManager.setReportContextTypeDefaultOrderArray(reportContextTypeDefaultOrderArray);
      if (!getSessionStorageItem('reportContextType')) {
        // we do not have a defined 'selected' reportContextType in sessionStorage, so
        // set selected reportContextType to be whichever is first in provided array
        reportContextManager.setReportContextType(reportContextTypeDefaultOrderArray[0]);
      }
    }
    if (typeof reportTypeSelectorViewIconOverride === 'object') {
      reportOverrideManager.setReportTypeSelectorViewIconOverride(reportTypeSelectorViewIconOverride);
    }
    if (isBoolean(restrictAvatar)) {
      userManager.setRestrictAvatar(restrictAvatar);
    }
    if (isBoolean(restrictAvatarForAllUserTypes)) {
      userManager.setRestrictAvatarForAllUserTypes(restrictAvatarForAllUserTypes);
    }
    if (isBoolean(allowSsoViewClassroomSettings)) {
      classroomManager.setAllowSsoViewClassroomSettings(allowSsoViewClassroomSettings);
    }
    if (isBoolean(shouldAlwaysOpenKebabOnProductLineLibraryCardClick)) {
      libraryManager.setShouldAlwaysOpenKebabOnProductLineLibraryCardClick(shouldAlwaysOpenKebabOnProductLineLibraryCardClick);
    }
    if (isBoolean(shouldRedirectAddCourseLauncherButtonToLibrary)) {
      courseManager.setShouldRedirectAddCourseLauncherButtonToLibrary(shouldRedirectAddCourseLauncherButtonToLibrary);
    }
    if (isBoolean(shouldOverrideBackendDefaultClassroomImageWithCustomStyling)) {
      classroomManager.setShouldOverrideBackendDefaultClassroomImageWithCustomStyling(
        shouldOverrideBackendDefaultClassroomImageWithCustomStyling
      );
    }
    if (isBoolean(shouldShowStandardsReportSummaryBar)) {
      reportOverrideManager.setShouldShowStandardsReportSummaryBar(shouldShowStandardsReportSummaryBar);
    }
    if (isBoolean(showGradebookKeyViewMoreModal)) {
      gradebookManager.setShowGradebookKeyViewMoreModal(showGradebookKeyViewMoreModal);
    }
    if (isBoolean(showManualScoringToolbarItem)) {
      courseDeliveryManager.setShowManualScoringToolbarItem(showManualScoringToolbarItem);
    }
    if (isBoolean(showResourcePacingToolbar)) {
      courseDeliveryManager.setShowResourcePacingToolbar(showResourcePacingToolbar);
    }
    if (Array.isArray(studentAssignmentMenuStatusOrderArray)) {
      studentAssignmentManager.setStudentAssignmentMenuStatusOrderArray(studentAssignmentMenuStatusOrderArray);
    }
    if ('studentContentCardDateFormatStrategy' in flagOverrides) {
      studentContentCardManager.setStudentContentCardDateFormatStrategy(studentContentCardDateFormatStrategy);
    }
    if (isBoolean(allowTeacherAssignmentListLauncherButton)) {
      assignmentManager.setAllowTeacherAssignmentListLauncherButton(allowTeacherAssignmentListLauncherButton);
    }
    if (isBoolean(allowTeacherAssignmentShowAllLauncherButton)) {
      assignmentManager.setAllowTeacherAssignmentShowAllLauncherButton(allowTeacherAssignmentShowAllLauncherButton);
    }
    if (typeof teacherAssignmentDefaultMenuStatus === 'string') {
      assignmentManager.setTeacherAssignmentDefaultMenuStatus(teacherAssignmentDefaultMenuStatus);
    }
    if (Array.isArray(teacherAssignmentMenuStatusOrderArray)) {
      assignmentManager.setTeacherAssignmentMenuStatusOrderArray(teacherAssignmentMenuStatusOrderArray);
    }
    if (Array.isArray(teacherClassroomCardFooterNavButtonOrderArray)) {
      classroomManager.setTeacherClassroomCardFooterNavButtonOrderArray(teacherClassroomCardFooterNavButtonOrderArray);
    }
    if (typeof testBuilderContextHelpId === 'string') {
      contextHelpManager.setTestBuilderContextHelpId(testBuilderContextHelpId);
    }
    if (typeof thirdPartyChatLauncherCustomVariables === 'object') {
      thirdPartyManager.setThirdPartyChatLauncherCustomVariables(thirdPartyChatLauncherCustomVariables);
    }
    if (typeof thirdPartyChatLauncherUrl === 'string') {
      thirdPartyManager.setThirdPartyChatLauncherUrl(thirdPartyChatLauncherUrl);
    }
    if (isBoolean(useAssignmentTabRollovers)) {
      assignmentManager.setUseAssignmentTabRollovers(useAssignmentTabRollovers);
    }
    if (isBoolean(useProductLineLibrary)) {
      libraryManager.setUseProductLineLibrary(useProductLineLibrary);
    }
    if (isBoolean(wordToolsTTSDependent)) {
      accommodationsManager.setWordToolsTTSDependent(wordToolsTTSDependent);
    }
  };

  static initDynamicSatelliteTranslations = async ({ satelliteJsonUrl } = {}) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    const dynamicSatelliteTranslationResponse = satelliteJsonUrl ? await fetch(satelliteJsonUrl, {
      headers,
      method: 'GET',
      redirect: 'follow'
    }) : undefined;

    const dynamicSatelliteTranslations = await dynamicSatelliteTranslationResponse?.json?.();

    // merge the current dynamic satellite translations with the default sat-core translations
    i18nAddSatellite('dynamic', dynamicSatelliteTranslations || {});
  }

  static initDynamicSatelliteStyleSheet = ({ satelliteCssUrl } = {}) => {
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.type = 'text/css';

    cssLink.href = satelliteCssUrl;
    cssLink.onload = () => {
      dynamicSatelliteManager.setIsDynamicSatelliteCssLoaded(true);
    };
    cssLink.onerror = () => {
      // do not hang the app if the CSS fails to load
      // render normally, but log the failure for visibility and debugging purposes
      console.error(`Failed to load dynamic satellite CSS from url: ${satelliteCssUrl}`);
      dynamicSatelliteManager.setIsDynamicSatelliteCssLoaded(true);
    };

    document.querySelector('head').appendChild(cssLink);
  }

  /**
   * Redirect to the user's assigned publisher satellite url when the satellite url
   * they logged into does not match their assigned one. Cleans up the local session
   * and re-auths on the target via Verification.js (`?authKey=...`).
   *
   * @returns {boolean} true if a redirect was issued (caller should return early)
   */
  static redirectToPublisherSatelliteIfApplicable = () => {
    const userSatelliteUrl = userManager.user?.publisherSatelliteUrl;

    // TODO useful for local dev to test the publisher satellite redirect
    // TODO remember to undo changes before committing
    // const userSatelliteUrl = 'http://localhost:3002';

    // strip any trailing slash to ensure potential match with window.location.origin
    // and to avoid `//verification` in the redirect URL concat below
    const strippedUserSatelliteUrl = (userSatelliteUrl || '').replace(/\/$/, '');

    if (!userSatelliteUrl || (userSatelliteUrl === Auth.publisherSatelliteUrl) || (
      strippedUserSatelliteUrl === window.location.origin
    )) {
      return false;
    }

    // preserve authKey before cleanup wipes userManager state
    const { authKey } = userManager;

    const InitService = getRegisteredClass('InitService');
    InitService.doLogoutCleanUp('', { authKey });

    const redirectUrl = generateRobustUrl(
      `${strippedUserSatelliteUrl}/verification`,
      { authKey }
    );
    window.location.href = redirectUrl;
    return true;
  }

  static initPostLoginSettersForDynamicSatellite = () => {
    // init satellite fav icon if it is not already set
    this.initDynamicFavIconIfApplicable();

    // set top nav 'Help/Resources' url for Student role if applicable
    // if (userManager.isStudent) {
    const cssUrlValue_topNavResourceUrlStudent = getStyleVar('--top-nav-dynamic-resource-url-student');
    const topNavResourceUrlStudent = getCssUrlArgument(cssUrlValue_topNavResourceUrlStudent);
    contentManager.setResourceIFrameUrlStudent(topNavResourceUrlStudent);
    // }

    // set top nav 'Help/Resources' url for non-Student role if applicable
    // if (!userManager.isStudent) {
    const cssUrlValue_topNavResourceUrl = getStyleVar('--top-nav-dynamic-resource-url');
    const topNavResourceUrl = getCssUrlArgument(cssUrlValue_topNavResourceUrl);
    contentManager.setResourceIFrameUrl(topNavResourceUrl);
    // }

    const cssUrlValue_topNavResourceUrlTarget = getStyleVar('--top-nav-dynamic-resource-url-target');
    const topNavResourceUrlTarget = getCssUrlArgument(cssUrlValue_topNavResourceUrlTarget);
    contentManager.setResourceIFrameUrlTarget(topNavResourceUrlTarget);

    // set useWhiteInfoIcon to true/false based on `fontColorDirectlyOnBackground`
    const fontColorOnBackground = userManager.isStudent ? (
      getStyleVar('--theme-font-color-directly-on-background-student')
    ) : (
      getStyleVar('--theme-font-color-directly-on-background')
    );
    const shouldUseWhiteInfoIcon = isWhiteStyleColor(fontColorOnBackground);
    dialogManager.setUseWhiteInfoIcon(shouldUseWhiteInfoIcon);
  }

  /**
   * Manually inject fav icon for a given satellite if it is not already in the DOM.
   *
   * Intended for dynamic satellites where the fav icon url is not hardcoded in `index.html`.
   *
   * If fav icon url is declared in `index.html` and never changes, no need to use this method. */
  static initDynamicFavIconIfApplicable = () => {
    try {
      let favIconLink = document.querySelector("link[rel~='icon']");

      const cssUrlValue = getStyleVar('--fav-icon');
      const favIconUrl = getCssUrlArgument(cssUrlValue);

      if (favIconLink?.href?.includes?.(favIconUrl)) {
        // we have already initialized the fav icon, so no need to run additional logic
        return;
      }

      favIconLink = document.createElement('link');
      favIconLink.rel = 'icon';
      document.head.appendChild(favIconLink);

      favIconLink.href = favIconUrl;
    } catch (error) {
      console.error(error);
    }
  }
}

registerClass('DynamicSatelliteService', DynamicSatelliteService);
